import { AudioControlsContainer } from "../../../AudioControlsContainer";
import { AudioManager } from "../../../AudioManager";
import { GameConstants } from "../../../GameConstants";
import { GameManager } from "../../../GameManager";
import { GameVars } from "../../../GameVars";
import { Button } from "../../../utils/Button";
import { ButtonWithText } from "../../../utils/ButtonWithText";
import { SplashScene } from "../SplashScene";
import { LanguageSelectionMenu } from "./LanguageSelectionMenu";

export class SettingsLayer extends Phaser.GameObjects.Container {

    private progressLabel: Phaser.GameObjects.Text;
    private resetProgressButton: Button;
    private resetButton: ButtonWithText;
    private cancelButton: ButtonWithText;
    private multilingualButton: Button;
    private scaledItemsContainer: Phaser.GameObjects.Container;
    private boundingBox: Phaser.Geom.Rectangle;
    private languageSelectionMenu: LanguageSelectionMenu;

    constructor(scene: Phaser.Scene) {
        
        super(scene);

        const gameText = this.scene.cache.json.get("text");
        const lan = GameVars.gameData.data.language;

        this.languageSelectionMenu = null;

        const darkLayer = new Phaser.GameObjects.Graphics(this.scene);
        darkLayer.fillStyle(0x2c241f);
        darkLayer.alpha = .5;
        darkLayer.fillRect(0, 0, GameConstants.GAME_WIDTH, GameConstants.GAME_HEIGHT);
        this.add(darkLayer);

        this.scaledItemsContainer = new Phaser.GameObjects.Container(this.scene, GameConstants.GAME_WIDTH / 2, GameConstants.GAME_HEIGHT / 2);
        this.scaledItemsContainer.scaleX = GameVars.scaleX;
        this.add( this.scaledItemsContainer);

        const background = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "layer_small");
        this.scaledItemsContainer.add(background);

        const menuTitleBackground = new Phaser.GameObjects.Image(this.scene, -325, -195, "texture_atlas_1", "tab_boards_daily_games");
        menuTitleBackground.setOrigin(0);
        this.scaledItemsContainer.add(menuTitleBackground);

        const menuTitleLabel = new Phaser.GameObjects.Text(this.scene, menuTitleBackground.x + 30, menuTitleBackground.y + 20, gameText.SETTINGS[lan], {fontFamily: "OpenSans-CondLight", fontSize: "52px", color: "#7C5D46"});
        menuTitleLabel.setOrigin(0);
        this.scaledItemsContainer.add(menuTitleLabel);

        const audioControlsContainer = new AudioControlsContainer(this.scene);
        audioControlsContainer.x = 55;
        audioControlsContainer.y = -15;
        this.scaledItemsContainer.add(audioControlsContainer);

        this.progressLabel = new Phaser.GameObjects.Text(this.scene, 25, 160, gameText.RESET_PROGRESS[lan], {fontFamily: "OpenSans-CondLight", fontSize: "36px", color: "#f6f5eb"});
        this.progressLabel.setOrigin(1, .5);
        this.scaledItemsContainer.add(this.progressLabel);

        this.resetProgressButton = new Button(this.scene, 70, this.progressLabel.y, "texture_atlas_1", "btn_reset_progress_off", "btn_reset_progress_on");
        this.resetProgressButton.onDown(this.onClickResetProgressButton, this);
        this.scaledItemsContainer.add(this.resetProgressButton);

        const resetButtonImg = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "btn");
        const resetLabel = new Phaser.GameObjects.Text(this.scene, 0, 0, gameText.RESET[lan], {fontFamily: "OpenSans-CondLight", fontSize: "28px", color: "#e40a49"});
        resetLabel.setOrigin(.5);

        if (resetLabel.width > 100) {
            resetLabel.setScale(100 / resetLabel.width);
        }
       
        this.resetButton = new ButtonWithText(this.scene, resetButtonImg, resetLabel, this.onClickResetButton, this);
        this.resetButton.x = -68;
        this.resetButton.y = 160;
        this.resetButton.visible = false;
        this.scaledItemsContainer.add(this.resetButton);

        const cancelButtonImg = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "btn");
        const cancelLabel = new Phaser.GameObjects.Text(this.scene, 0, 0, gameText.CANCEL[lan], {fontFamily: "OpenSans-CondLight", fontSize: "28px", color: "#66635e"});
        cancelLabel.setOrigin(.5);

        if (cancelLabel.width > 100) {
            cancelLabel.setScale(100 / cancelLabel.width);
        }

        this.cancelButton = new ButtonWithText(this.scene, cancelButtonImg, cancelLabel, this.onClickCancelButton, this);
        this.cancelButton.x = -this.resetButton.x;
        this.cancelButton.y = this.resetButton.y;
        this.cancelButton.visible = false;
        this.scaledItemsContainer.add(this.cancelButton);

        const languageLabel = new Phaser.GameObjects.Text(this.scene, 225, -165, this.getCurrentLanguage(), {fontFamily: "OpenSans-CondLight", fontSize: "36px", color: "#7C5D46"});
        languageLabel.setOrigin(1, .5);
        this.scaledItemsContainer.add(languageLabel);

        this.multilingualButton = new Button(this.scene, 280, -165, "texture_atlas_1", "btn_multilingual", "btn_multilingual");
        this.multilingualButton.setInflationTween(1.1);
        this.multilingualButton.onDown(this.onClickLanguageSelection, this);
        this.scaledItemsContainer.add(this.multilingualButton);

        const w = 690 * GameVars.scaleX;
        const h = 430;  

        this.boundingBox = new Phaser.Geom.Rectangle(GameConstants.GAME_WIDTH / 2 - w / 2, GameConstants.GAME_HEIGHT / 2 - h / 2 - 10, w, h);

        if (GameConstants.DEVELOPMENT) {

            const boundingBoxGraphics = new Phaser.GameObjects.Graphics(this.scene);
            boundingBoxGraphics.lineStyle(2, 0x00ff00);
            boundingBoxGraphics.strokeRectShape(this.boundingBox);
            this.add(boundingBoxGraphics);
        }

        this.alpha = 0;

        const self = this;

        this.scene.tweens.add({
            targets: this,
            alpha: 1,
            duration: 350,
            ease: Phaser.Math.Easing.Cubic.Out,
            onComplete: function(): void {
                if (self.scene ) {
                    self.scene.input.on("pointerdown", self.onClickClose, self);
                } 
            },
            onCompleteScope: this
        });
    }

    private onClickResetProgressButton(): void {

        this.scene.time.delayedCall(150, function(): void {

            this.progressLabel.visible = false;
            this.resetProgressButton.visible = false;

            this.resetButton.visible = true;
            this.cancelButton.visible = true;

        }, [], this);

        AudioManager.playSound("btn_press");
    }

    private onClickResetButton(): void {
        
        GameManager.resetCalendar();

        AudioManager.playSound("btn_press");
    }

    private onClickCancelButton(): void {

        this.scene.time.delayedCall(100, function(): void {
            
            this.progressLabel.visible = true;
            this.resetProgressButton.visible = true;
    
            this.resetButton.visible = false;
            this.cancelButton.visible = false;

        }, [], this);

        AudioManager.playSound("btn_press");
    }

    private onClickClose(): void {

        // mirar las coordenadas del punto donde se hizo click
        if (!this.scene) {
            return;
        }

        if (this.boundingBox.contains(this.scene.input.x, this.scene.input.y)) {
            return;
        }

        if (this.languageSelectionMenu) {
            this.languageSelectionMenu.destroy();
            this.languageSelectionMenu = null;
        } else {
            this.scene.tweens.add({
                targets: this,
                alpha: 0,
                duration: 350,
                ease: Phaser.Math.Easing.Cubic.Out,
                onComplete: function(): void {
                    SplashScene.currentInstance.hideSettingsLayer();
                },
                onCompleteScope: this
            });
        }

        AudioManager.playSound("btn_press");
    }

    private onClickLanguageSelection(): void {

        this.languageSelectionMenu = new LanguageSelectionMenu(this.scene);
        this.scaledItemsContainer.add(this.languageSelectionMenu);

        AudioManager.playSound("btn_press");
    }

    private getCurrentLanguage(): string {

        switch (GameVars.gameData.data.language) {

            case "en":
                return "English";
            case "es":
                return "Español";
            case "ca":
                return "Català";
            case "de":
                return "Deutsch";
            case "fr":
                return "Français";
            case "it":
                return "Italiano";
            case "pt":
                return "Português - BR";
            case "nl":
                return "Nederlands";
            case "ru":
                return "Русский";
            case "ja":
                return "日本語";
            default:
                return "English";
        }
    }
}
